import React, { useState, useEffect, useCallback } from 'react';
import { ExternalLink, Play, Square, MessageSquare, Wrench, CheckCircle, XCircle, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getAuthHeaders } from '@/services/authConfig';

interface TraceEvent {
  id: string;
  type: string;
  name?: string;
  timestamp: string;
  data?: any;
}

interface PlaygroundObserverProps {
  logs: string;
  traceEvents: TraceEvent[];
  trajectoryId: string | null;
  activeTab: 'logs' | 'trace';
}

// Get icon and color for event type
function getEventStyle(type: string) {
  switch (type) {
    case 'trajectory_start':
      return { icon: Play, color: 'text-blue-500', bg: 'bg-blue-500/10' };
    case 'trajectory_end':
      return { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-500/10' };
    case 'trajectory_failed':
      return { icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10' };
    case 'observation_emitted':
      return { icon: MessageSquare, color: 'text-purple-500', bg: 'bg-purple-500/10' };
    case 'tool_called':
    case 'tool_result':
      return { icon: Wrench, color: 'text-orange-500', bg: 'bg-orange-500/10' };
    default:
      return { icon: Square, color: 'text-gray-500', bg: 'bg-gray-500/10' };
  }
}

// Format event type for display
function formatEventType(type: string) {
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function TraceEventItem({ event }: { event: TraceEvent }) {
  const [expanded, setExpanded] = useState(false);
  const style = getEventStyle(event.type);
  const Icon = style.icon;

  // Extract displayable content
  const content = event.data?.content;
  const toolName = event.data?.tool_name;
  const toolCalls = event.data?.tool_calls;
  const hasExpandableContent = content || toolCalls?.length > 0;

  return (
    <div className={cn("text-xs rounded border overflow-hidden", style.bg)}>
      <div
        className={cn(
          "flex items-center gap-2 p-2 cursor-pointer hover:bg-muted/30",
          hasExpandableContent && "cursor-pointer"
        )}
        onClick={() => hasExpandableContent && setExpanded(!expanded)}
      >
        {hasExpandableContent ? (
          expanded ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />
        ) : (
          <Icon className={cn("h-3 w-3 shrink-0", style.color)} />
        )}
        <span className="font-medium flex-1 truncate">
          {formatEventType(event.type)}
          {event.name && <span className="text-muted-foreground ml-1">({event.name})</span>}
          {toolName && <span className="text-muted-foreground ml-1">({toolName})</span>}
        </span>
        <span className="text-muted-foreground text-[10px] shrink-0">
          {new Date(event.timestamp).toLocaleTimeString()}
        </span>
      </div>

      {expanded && hasExpandableContent && (
        <div className="px-2 pb-2 pt-1 border-t border-border/50">
          {content && (
            <pre className="whitespace-pre-wrap text-[11px] text-muted-foreground font-mono max-h-32 overflow-auto">
              {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
            </pre>
          )}
          {toolCalls?.length > 0 && (
            <div className="space-y-1">
              {toolCalls.map((tc: any, idx: number) => (
                <div key={idx} className="text-[11px]">
                  <span className="font-medium">{tc.name}</span>
                  {tc.args && (
                    <pre className="text-muted-foreground font-mono text-[10px] mt-0.5 max-h-20 overflow-auto">
                      {JSON.stringify(tc.args, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Apply syntax highlighting to log lines
function highlightLogs(logContent: string) {
  if (!logContent) return null;

  return logContent.split('\n').map((line, index) => {
    let colorClass = 'text-muted-foreground';

    if (line.includes('[ERROR]')) {
      colorClass = 'text-red-400';
    } else if (line.includes('[WARNING]')) {
      colorClass = 'text-yellow-400';
    } else if (line.includes('[DEBUG]')) {
      colorClass = 'text-gray-500';
    } else if (line.includes('[INFO]')) {
      colorClass = 'text-blue-400';
    }

    return (
      <div key={index} className={cn('leading-relaxed', colorClass)}>
        {line}
      </div>
    );
  });
}

export function PlaygroundObserver({
  logs,
  traceEvents,
  trajectoryId,
  activeTab,
}: PlaygroundObserverProps) {
  const [s3Logs, setS3Logs] = useState<string | null>(null);
  const [logSource, setLogSource] = useState<'s3' | 'events' | null>(null);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Fetch S3 run logs
  const fetchS3Logs = useCallback(async () => {
    if (!trajectoryId) return;

    setLoadingLogs(true);
    try {
      const response = await fetch(`/api/trajectories/${trajectoryId}/run-logs`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const data = await response.json();

      if (data.log_content && !data.error) {
        setS3Logs(data.log_content);
        setLogSource('s3');
      } else {
        setS3Logs(null);
        setLogSource('events');
      }
    } catch (err) {
      console.error('Error fetching S3 logs:', err);
      setS3Logs(null);
      setLogSource('events');
    } finally {
      setLoadingLogs(false);
    }
  }, [trajectoryId]);

  // Fetch S3 run logs when trajectoryId changes or when switching to logs tab
  useEffect(() => {
    if (activeTab === 'logs' && trajectoryId) {
      fetchS3Logs();
    }
  }, [trajectoryId, activeTab, fetchS3Logs]);

  // Determine which logs to display
  const displayLogs = s3Logs || logs;

  return (
    <div className="h-full flex flex-col">
      {/* Header with link to full trajectory view */}
      {trajectoryId && (
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="flex items-center gap-2">
            {activeTab === 'logs' && logSource && (
              <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
                {logSource === 's3' ? 'Run Logs' : 'Events'}
              </span>
            )}
            {activeTab === 'logs' && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={fetchS3Logs}
                disabled={loadingLogs}
              >
                <RefreshCw className={cn("h-3 w-3", loadingLogs && "animate-spin")} />
              </Button>
            )}
          </div>
          <Button variant="ghost" size="sm" asChild className="h-7 text-xs">
            <Link to={`/trajectory/${trajectoryId}`} target="_blank">
              <ExternalLink className="h-3 w-3 mr-1" />
              Full View
            </Link>
          </Button>
        </div>
      )}

      {/* Content based on active tab */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'logs' ? (
          <ScrollArea className="h-full">
            <div className="p-3">
              {loadingLogs ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  Loading logs...
                </div>
              ) : displayLogs && displayLogs !== 'No logs available' ? (
                <div className="bg-[#1e1e1e] rounded-lg p-3">
                  <pre className="text-xs font-mono whitespace-pre-wrap">
                    {highlightLogs(displayLogs)}
                  </pre>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Logs will appear here during execution.
                </p>
              )}
            </div>
          </ScrollArea>
        ) : (
          <ScrollArea className="h-full">
            <div className="p-3 space-y-1.5">
              {traceEvents.length > 0 ? (
                traceEvents.map((event) => (
                  <TraceEventItem key={event.id} event={event} />
                ))
              ) : (
                <p className="text-xs text-muted-foreground">
                  Trace events will appear here during execution.
                </p>
              )}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}

export default PlaygroundObserver;
